import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import moment from "moment";
import styles from "../styles/shared/BotPerformanceChart.module.css";

const BotPerformanceChart = ({
  balanceLists = [],
  balanceDateList = [],
  showOption = true,
  wrapperClassName = "",
  header,
}) => {
  const [range, setRange] = useState(showOption ? "1M" : "ALL"); // 1M = One Month

  const filteredData = useMemo(() => {
    const now =
      moment.utc(balanceDateList?.[balanceDateList?.length - 1]).local() ||
      moment();
    let startDate = null;

    switch (range) {
      case "1W":
        startDate = now.clone().subtract(1, "week");
        break;
      case "3M":
        startDate = now.clone().subtract(3, "months");
        break;
      case "6M":
        startDate = now.clone().subtract(6, "months");
        break;
      case "ALL":
        startDate = null;
        break;
      case "1M":
      default:
        startDate = now.clone().subtract(1, "months");
    }

    return balanceDateList
      .map((date, index) => {
        const raw = Number(balanceLists[index]);
        return {
          x: new Date(date).getTime(),
          y: Number.isFinite(raw) ? parseFloat(raw.toFixed(4)) : 0,
          date: moment(date),
        };
      })
      .filter((item) => !startDate || item.date.isSameOrAfter(startDate))
      .map(({ x, y }) => ({ x, y }));
  }, [balanceLists, balanceDateList, range]);

  const series = [
    {
      name: "Profit (%)",
      data: filteredData,
      color: "#0C4CFC",
    },
  ];

  const yDigits = useMemo(() => {
    if (!filteredData.length) return 2;
    const ys = filteredData.map((point) => point.y);
    const span = Math.max(...ys) - Math.min(...ys);
    return span < 0.1 ? 4 : 2;
  }, [filteredData]);

  const formatY = (value) => `${Number(value).toFixed(yDigits)}%`;

  const options = {
    chart: {
      id: "balance-chart",
      type: "line",
      zoom: {
        enabled: true,
      },
      toolbar: {
        show: true,
      },
      background: "#ffffff",
      foreColor: "#333333",
      fontFamily: "inherit",
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 2,
    },
    yaxis: {
      labels: {
        formatter: formatY,
      },
    },
    tooltip: {
      theme: "light",
      x: {
        formatter: function (value) {
          return moment(value).format("DD MMM YYYY HH:mm");
        },
      },
      y: {
        formatter: formatY,
      },
    },
    xaxis: {
      type: "datetime",
      labels: {
        formatter: function (value) {
          return moment(value).format("DD MMM YYYY");
        },
      },
    },
    // title: {
    //   text: "NAV Profit (%)",
    //   align: "center",
    // },
    grid: {
      show: true,
      borderColor: "#e0e0e0",
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  };

  return (
    <div className={wrapperClassName}>
      <div className={styles.header}>
        <h5>NAV Profit (%)</h5>
      </div>
      {showOption && (
        <div className={styles.selectRange}>
          <ul className={styles.options}>
            <li
              className={`${styles.option} ${range === "1M" ? styles.active : ""}`}
              onClick={() => setRange("1M")}
            >
              1M
            </li>
            <li
              className={`${styles.option} ${range === "3M" ? styles.active : ""}`}
              onClick={() => setRange("3M")}
            >
              3M
            </li>
            <li
              className={`${styles.option} ${range === "6M" ? styles.active : ""}`}
              onClick={() => setRange("6M")}
            >
              6M
            </li>
            <li
              className={`${styles.option} ${range === "ALL" ? styles.active : ""}`}
              onClick={() => setRange("ALL")}
            >
              All
            </li>
            {header && <li>{header}</li>}
          </ul>
        </div>
      )}
      <Chart
        options={options}
        series={series}
        type="line"
        width="100%"
        height="400"
      />
    </div>
  );
};

export default BotPerformanceChart;
